// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { PartCatalog, PartListPart, WithRequiredNotNull } from '@evolvemep/api-ts';
import { Stack, Text, Overlay, Divider } from '@mantine/core';

import { isNil } from 'helpers/isNotNil';
import type { NeedsRefreshItem, CatalogSubComponentRenderOpts } from 'modules/Shop/CatalogSetup/CatalogIntrospector';
import { CatalogPartList } from 'modules/Shop/CatalogSetup/CatalogPartList';
import { CatalogSearch } from 'modules/Shop/CatalogSetup/CatalogSearch';
import { CatalogSelect } from 'modules/Shop/CatalogSetup/CatalogSelect';
import { ExistingCategorySelector } from 'modules/Shop/CatalogSetup/ExistingCategorySelector';
import { useCatalogIntrospector } from 'modules/Shop/CatalogSetup/useCatalogIntrospector';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

export type MobileCatalogIntrospectorProps = {
  partCatalog: PartCatalog | null | undefined;
  /**
   * There's no modal header space for a catalog select on mobile like there is on desktop,
   * so `MobileCatalogIntrospector` renders one itself.
   */
  onCatalogSelected: (catalog: PartCatalog | null) => void;
  onPartSelected?: (part: PartListPart | null) => void;
  /** Component to render below the Search field */
  belowSearchComponent?: ((opts: CatalogSubComponentRenderOpts) => ReactNode) | ReactNode;
  disablePart?: (part: PartListPart) => boolean;
  /** Component to render on the right side of the PartCard */
  children?: (part: PartListPart) => ReactNode;
  disabled?: boolean;
  needsRefresh?: NeedsRefreshItem[];
  useQueryParams?: boolean;
  defaultCategory?: CategoryPath[number];
};

const MobileCatalogIntrospectorWrapped = ({
  partCatalog,
  onCatalogSelected,
  onPartSelected: onPartSelectedParent,
  belowSearchComponent,
  children,
  disablePart,
  disabled,
  needsRefresh,
  useQueryParams,
  defaultCategory,
}: WithRequiredNotNull<MobileCatalogIntrospectorProps, 'partCatalog'>) => {
  const {
    categoryPath,
    selectedCategory,
    selectedPart,
    loadingCategoryPath,
    loadingCategory,
    loadingCategoryFromPath,
    parts,
    fetchNextPageParts,
    loadingParts,
    onCategorySelected,
    onPartSelected,
  } = useCatalogIntrospector({
    partCatalog,
    onPartSelected: onPartSelectedParent,
    needsRefresh,
    useQueryParams,
    defaultCategory,
  });

  return (
    <Stack flex={1} gap="sm" p="md" style={{ overflow: 'hidden' }} pos="relative">
      <CatalogSelect selectedCatalog={partCatalog} onCatalogSelected={onCatalogSelected} showLabel w="100%" />
      <ExistingCategorySelector
        partCatalog={partCatalog}
        value={selectedCategory}
        onChange={onCategorySelected}
        loading={loadingCategoryPath || loadingCategory || loadingCategoryFromPath}
      />

      <Divider my="xs" />

      <CatalogSearch
        partCatalog={partCatalog}
        onPartSelected={(part) => onPartSelected(part, true)}
        onCategorySelected={onCategorySelected}
        w="100%"
      />

      {typeof belowSearchComponent === 'function'
        ? belowSearchComponent({ partCatalog, categoryPath, onPartSelected, selectedCategory })
        : belowSearchComponent}

      <CatalogPartList
        parts={parts}
        selectedCategory={selectedCategory}
        selectedPart={selectedPart}
        onPartClick={onPartSelectedParent ? (part) => onPartSelected(part, false) : undefined}
        disablePart={disablePart}
        fetchNextPage={fetchNextPageParts}
        loading={loadingParts}
        fullWidthCards
      >
        {children}
      </CatalogPartList>
      {disabled && <Overlay bg="white" opacity={0.5} />}
    </Stack>
  );
};

/** Mobile, single-column version of `CatalogIntrospector` used to navigate the catalog and do "something" with parts. */
export const MobileCatalogIntrospector = ({
  partCatalog,
  onCatalogSelected,
  ...props
}: MobileCatalogIntrospectorProps) =>
  isNil(partCatalog) ? (
    <Stack gap="sm" p="md">
      <CatalogSelect selectedCatalog={null} onCatalogSelected={onCatalogSelected} showLabel w="100%" />
      <Text c="dimmed" ta="center">
        Select a catalog.
      </Text>
    </Stack>
  ) : (
    <MobileCatalogIntrospectorWrapped {...props} partCatalog={partCatalog} onCatalogSelected={onCatalogSelected} />
  );
